import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { Scene3D, SceneObject, CameraState, SceneSettings } from '../types';

interface SceneStore {
  // State
  scenes: Scene3D[];
  currentSceneId: string | null;
  currentScene: Scene3D | null;
  loading: boolean;
  error: string | null;

  // Scene management
  createScene: (scene: Partial<Scene3D>) => string;
  loadScene: (sceneId: string) => void;
  updateScene: (updates: Partial<Scene3D>) => void;
  deleteScene: (sceneId: string) => void;
  duplicateScene: (sceneId: string) => string;

  // Object management
  addObject: (object: Partial<SceneObject>) => string;
  updateObject: (objectId: string, updates: Partial<SceneObject>) => void;
  deleteObject: (objectId: string) => void;
  duplicateObject: (objectId: string) => string;
  moveObject: (objectId: string, newPosition: [number, number, number]) => void;
  rotateObject: (objectId: string, newRotation: [number, number, number]) => void;
  scaleObject: (objectId: string, newScale: [number, number, number]) => void;

  // Bulk operations
  deleteObjects: (objectIds: string[]) => void;
  duplicateObjects: (objectIds: string[]) => string[];
  groupObjects: (objectIds: string[]) => string;
  ungroupObjects: (groupId: string) => void;

  // Scene operations
  clearScene: () => void;
  resetCamera: () => void;
  updateCamera: (camera: Partial<CameraState>) => void;
  updateSettings: (settings: Partial<SceneSettings>) => void;

  // Persistence
  saveToLocalStorage: () => void;
  loadFromLocalStorage: () => void;
  exportScene: (sceneId?: string) => string;
  importScene: (sceneData: string) => string;

  // Utilities
  getObjectById: (objectId: string) => SceneObject | null;
  getObjectsByType: (type: string) => SceneObject[];
  getChildObjects: (parentId: string) => SceneObject[];
  getParentObject: (objectId: string) => SceneObject | null;
}

const DEFAULT_SCENE_SETTINGS: SceneSettings = {
  units: 'metric',
  gridSize: 100,
  gridDivisions: 100,
  showGrid: true,
  showAxes: true,
  shadows: true,
  ambientLight: {
    intensity: 0.4,
    color: '#ffffff',
  },
  backgroundColor: '#f0f0f0',
};

const DEFAULT_CAMERA: CameraState = {
  position: [10, 10, 10],
  target: [0, 0, 0],
  fov: 45,
  near: 0.1,
  far: 1000,
};

export const useSceneStore = create<SceneStore>()(
  persist(
    immer((set, get) => ({
      // Initial state
      scenes: [],
      currentSceneId: null,
      currentScene: null,
      loading: false,
      error: null,

      // Scene management
      createScene: (sceneData) => {
        const id = uuidv4();
        const scene: Scene3D = {
          id,
          name: sceneData.name ?? 'New Scene',
          description: sceneData.description ?? '',
          type: sceneData.type ?? 'custom',
          settings: { ...DEFAULT_SCENE_SETTINGS, ...sceneData.settings },
          objects: sceneData.objects ?? [],
          camera: { ...DEFAULT_CAMERA, ...sceneData.camera },
          metadata: {
            created: new Date(),
            modified: new Date(),
            version: '1.0.0',
          },
        };

        set((state) => {
          state.scenes.push(scene);
          state.currentSceneId = id;
          state.currentScene = scene;
        });

        return id;
      },

      loadScene: (sceneId) => {
        set((state) => {
          const scene = state.scenes.find((s) => s.id === sceneId);
          if (scene) {
            state.currentSceneId = sceneId;
            state.currentScene = scene;
          }
        });
      },

      updateScene: (updates) => {
        set((state) => {
          if (state.currentScene) {
            Object.assign(state.currentScene, updates);
            state.currentScene.metadata.modified = new Date();

            // Update in scenes array
            const index = state.scenes.findIndex((s) => s.id === state.currentSceneId);
            if (index !== -1) {
              state.scenes[index] = state.currentScene;
            }
          }
        });
      },

      deleteScene: (sceneId) => {
        set((state) => {
          state.scenes = state.scenes.filter((s) => s.id !== sceneId);
          if (state.currentSceneId === sceneId) {
            state.currentSceneId = null;
            state.currentScene = null;
          }
        });
      },

      duplicateScene: (sceneId) => {
        const { scenes } = get();
        const scene = scenes.find((s) => s.id === sceneId);
        if (!scene) return '';

        const newId = uuidv4();
        const duplicatedScene: Scene3D = {
          ...scene,
          id: newId,
          name: `${scene.name} (Copy)`,
          metadata: {
            created: new Date(),
            modified: new Date(),
            version: scene.metadata.version,
          },
        };

        set((state) => {
          state.scenes.push(duplicatedScene);
        });

        return newId;
      },

      // Object management
      addObject: (objectData) => {
        const id = uuidv4();
        const object: SceneObject = {
          id,
          type: objectData.type ?? 'primitive',
          name: objectData.name ?? 'New Object',
          position: objectData.position ?? [0, 0, 0],
          rotation: objectData.rotation ?? [0, 0, 0],
          scale: objectData.scale ?? [1, 1, 1],
          visible: objectData.visible ?? true,
          locked: objectData.locked ?? false,
          properties: objectData.properties ?? {},
          children: objectData.children ?? [],
          parent: objectData.parent,
        };

        set((state) => {
          if (state.currentScene) {
            state.currentScene.objects.push(object);
            state.currentScene.metadata.modified = new Date();
          }
        });

        return id;
      },

      updateObject: (objectId, updates) => {
        set((state) => {
          if (state.currentScene) {
            const objectIndex = state.currentScene.objects.findIndex((obj) => obj.id === objectId);
            if (objectIndex !== -1) {
              Object.assign(state.currentScene.objects[objectIndex], updates);
              state.currentScene.metadata.modified = new Date();
            }
          }
        });
      },

      deleteObject: (objectId) => {
        const deleteObjectsRecursive = (objId: string, toDelete: Set<string>) => {
          toDelete.add(objId);
          const { currentScene } = get();
          if (currentScene) {
            currentScene.objects.forEach((obj) => {
              if (obj.parent === objId) {
                deleteObjectsRecursive(obj.id, toDelete);
              }
            });
          }
        };

        set((state) => {
          if (state.currentScene) {
            const toDelete = new Set<string>();
            deleteObjectsRecursive(objectId, toDelete);

            state.currentScene.objects = state.currentScene.objects.filter((obj) => !toDelete.has(obj.id));

            state.currentScene.metadata.modified = new Date();
          }
        });
      },

      duplicateObject: (objectId) => {
        const { currentScene } = get();
        if (!currentScene) return '';

        const object = currentScene.objects.find((obj) => obj.id === objectId);
        if (!object) return '';

        const newId = uuidv4();
        const duplicatedObject: SceneObject = {
          ...object,
          id: newId,
          name: `${object.name} (Copy)`,
          position: [object.position[0] + 1, object.position[1], object.position[2] + 1],
        };

        set((state) => {
          if (state.currentScene) {
            state.currentScene.objects.push(duplicatedObject);
            state.currentScene.metadata.modified = new Date();
          }
        });

        return newId;
      },

      moveObject: (objectId, newPosition) => {
        get().updateObject(objectId, { position: newPosition });
      },

      rotateObject: (objectId, newRotation) => {
        get().updateObject(objectId, { rotation: newRotation });
      },

      scaleObject: (objectId, newScale) => {
        get().updateObject(objectId, { scale: newScale });
      },

      // Bulk operations
      deleteObjects: (objectIds) => {
        objectIds.forEach((id) => get().deleteObject(id));
      },

      duplicateObjects: (objectIds) => {
        return objectIds.map((id) => get().duplicateObject(id));
      },

      groupObjects: (objectIds) => {
        const groupId = uuidv4();
        const groupObject: SceneObject = {
          id: groupId,
          type: 'group',
          name: 'Group',
          position: [0, 0, 0],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          visible: true,
          locked: false,
          properties: { expanded: true },
          children: objectIds,
        };

        const updateChildParents = (state: any) => {
          objectIds.forEach((objectId) => {
            const object = state.currentScene!.objects.find((obj: SceneObject) => obj.id === objectId);
            if (object) {
              object.parent = groupId;
            }
          });
        };

        set((state) => {
          if (state.currentScene) {
            state.currentScene.objects.push(groupObject);
            updateChildParents(state);
            state.currentScene.metadata.modified = new Date();
          }
        });

        return groupId;
      },

      ungroupObjects: (groupId) => {
        const updateChildrenParents = (children: string[], scene: Scene3D) => {
          children.forEach((childId) => {
            const child = scene.objects.find((obj) => obj.id === childId);
            if (child) {
              child.parent = undefined;
            }
          });
        };

        set((state) => {
          if (state.currentScene) {
            const group = state.currentScene.objects.find((obj) => obj.id === groupId);
            if (group?.children) {
              updateChildrenParents(group.children, state.currentScene);

              state.currentScene.objects = state.currentScene.objects.filter((obj) => obj.id !== groupId);

              state.currentScene.metadata.modified = new Date();
            }
          }
        });
      },

      // Scene operations
      clearScene: () => {
        set((state) => {
          if (state.currentScene) {
            state.currentScene.objects = [];
            state.currentScene.metadata.modified = new Date();
          }
        });
      },

      resetCamera: () => {
        set((state) => {
          if (state.currentScene) {
            state.currentScene.camera = { ...DEFAULT_CAMERA };
            state.currentScene.metadata.modified = new Date();
          }
        });
      },

      updateCamera: (camera) => {
        set((state) => {
          if (state.currentScene) {
            Object.assign(state.currentScene.camera, camera);
            state.currentScene.metadata.modified = new Date();
          }
        });
      },

      updateSettings: (settings) => {
        set((state) => {
          if (state.currentScene) {
            Object.assign(state.currentScene.settings, settings);
            state.currentScene.metadata.modified = new Date();
          }
        });
      },

      // Persistence
      saveToLocalStorage: () => {
        const { scenes } = get();
        localStorage.setItem('visualization-scenes', JSON.stringify(scenes));
      },

      loadFromLocalStorage: () => {
        try {
          const stored = localStorage.getItem('visualization-scenes');
          if (stored) {
            const scenes = JSON.parse(stored);
            set((state) => {
              state.scenes = scenes;
            });
          }
        } catch (error) {
          console.error('Failed to load scenes from localStorage:', error);
        }
      },

      exportScene: (sceneId) => {
        const { scenes, currentSceneId } = get();
        const id = sceneId ?? currentSceneId;
        const scene = scenes.find((s) => s.id === id);
        return scene ? JSON.stringify(scene, null, 2) : '';
      },

      importScene: (sceneData) => {
        try {
          const scene = JSON.parse(sceneData);
          const newId = uuidv4();
          const importedScene: Scene3D = {
            ...scene,
            id: newId,
            name: `${scene.name} (Imported)`,
            metadata: {
              ...scene.metadata,
              created: new Date(),
              modified: new Date(),
            },
          };

          set((state) => {
            state.scenes.push(importedScene);
          });

          return newId;
        } catch (error) {
          console.error('Failed to import scene:', error);
          return '';
        }
      },

      // Utilities
      getObjectById: (objectId) => {
        const { currentScene } = get();
        if (!currentScene) return null;
        return currentScene.objects.find((obj) => obj.id === objectId) || null;
      },

      getObjectsByType: (type) => {
        const { currentScene } = get();
        if (!currentScene) return [];
        return currentScene.objects.filter((obj) => obj.type === type);
      },

      getChildObjects: (parentId) => {
        const { currentScene } = get();
        if (!currentScene) return [];
        return currentScene.objects.filter((obj) => obj.parent === parentId);
      },

      getParentObject: (objectId) => {
        const { currentScene } = get();
        if (!currentScene) return null;
        const object = currentScene.objects.find((obj) => obj.id === objectId);
        if (!object?.parent) return null;
        return currentScene.objects.find((obj) => obj.id === object.parent) ?? null;
      },
    })),
    {
      name: 'visualization-scene-store',
      partialize: (state) => ({
        scenes: state.scenes,
        currentSceneId: state.currentSceneId,
      }),
    },
  ),
);
